<template>
  <div>
    <form @submit.prevent="" :class="[errorClass, {hide: showConfirmationWindow || showFileErrorWindow || showSpinner}]">
      <button type="button" class="exit-button" @click="close()"><rp-sprite class="exit-icon" name="close"/></button>
      <div class="title">
        <template v-if="isCreateForm">
          Add Language
        </template>
        <template v-else>
          Edit Language
        </template>
      </div>
      <hr>
      <div class="row">
        <div class="label">{{ 'Language name:' | uppercase }}</div>
        <rp-textinput
          v-model="resource.name"
          placeholder="Enter a value"
          :error="nameError"
          @input="touch('name')"
          isRequired/>
      </div>
      <div class="row" v-if="isCreateForm">
        <div class="label">{{ 'Language locale:' | uppercase }}</div>
        <rp-textinput
          v-model="resource.code"
          placeholder="Enter a value"
          :error="codeError"
          @input="touch('code')"
          isRequired/>
      </div>
      
      <div class="file-container" @drop.prevent="dropFile" @dragover.prevent>
        <input @change="fileLoaded" ref="browseInput" type="file" name="myFile" accept=".csv" isRequired>
        <div v-if="!resource.file" class="file-label" @click="browseFileSystem()">
          Drag and drop CSV file here or 
          <span class="file-button">
            browse
          </span>
        </div>
        <div v-else class="file-uploaded-container" @click="browseFileSystem()">
          <span class="file-name file-label">
            <rp-sprite class="done-icon" name="done"/>
            {{resource.file.name}}
          </span>
          <span class="file-button">
            Replace
          </span>
        </div>
      </div>

      <div class="file-button download-button" @click="downloadCSV()">
        <template v-if="isCreateForm">
          Download CSV template
        </template>
        <template v-else>
          {{`Download ${oldName} (${resource.code}).csv`}}
        </template>
      </div>

      <hr>

      <div class="button-container">
        <rp-button class="create-button" :disabled="isInvalid" type="primary" @click="createOrUpdateLanguage">
          <template v-if="isCreateForm">
            {{ 'Create' | uppercase }}
          </template>
          <template v-else>
            {{ 'Update' | uppercase }}
          </template>
        </rp-button>
        <rp-button v-if="!isCreateForm" class="delete-button" @click="openConfirmationWindow()" type="normal">
          {{ 'Delete' | uppercase }}
        </rp-button>
      </div>
    </form>

    <rp-overlay :active="showConfirmationWindow">
      <rp-confirm
        icon="warning"
        :messageText="'Delete ' + '<b>' + oldName + '(' + resource.code + ')' + '</b>' + ' language'"
        :cancel="closeConfirmationWindow"
        :confirm="deleteLanguage"/>
    </rp-overlay>

    <rp-overlay :active="showFileErrorWindow">
      <rp-alert
        class="error-icon"
        icon="warning_rounded"
        message="Please provide a CSV file"
        :onClick="closeFileErrorWindow">
      </rp-alert>
    </rp-overlay>

    <rp-overlay :active="showSpinner">
      <rp-spinner />
    </rp-overlay>

  </div>
</template>

<script>
  import { validationMixin } from 'vuelidate'
  import { required, requiredIf } from 'vuelidate/lib/validators'

  import { errorMessages } from '../../../api/error_messages'
  import form from '../../../mixins/form'

  import Button from '../../../components/ui/inputs/Button'
  import TextInput from '../../../components/ui/inputs/TextInput'
  import Sprite from '../../../components/ui/Sprite'
  import Overlay from '../../../components/ui/Overlay'
  import Confirm from '../../../components/ui/DeleteConfirm'
  import Alert from '../../../components/ui/Alert'
  import Spinner from '../../../components/ui/Spinner'

  import { createLanguage, getCSVSampleTemplate, getCSVTemplate, deleteLanguage, updateLocaleName } from '../../../api/languages'

  export default {
    components: {
      'rp-button': Button,
      'rp-textinput': TextInput,
      'rp-sprite': Sprite,
      'rp-overlay': Overlay,
      'rp-confirm': Confirm,
      'rp-spinner': Spinner,
      'rp-alert': Alert
    },

    computed: {
      ...errorMessages([
        'name',
        'code'
      ])
    },

    name: 'LanguageForm',

    mixins: [validationMixin, form],

    props: {
      isCreateForm: Boolean
    },

    data() {
      return {
        oldName: '',
        showConfirmationWindow: false,
        showFileErrorWindow: false,
        showSpinner: false
      }
    },

    mounted () {
      this.oldName = this.resource.name
    },

    methods: {
      close() {
        this.clearData();
        this.$emit('close');
      },

      fileLoaded(event) {
        this.resource.file = event.target.files[0]
      },

      dropFile(event) {
        const file = event.dataTransfer.files[0]
        if(file.type.includes("csv") || file.name.toLowerCase().endsWith('.csv'))
          this.resource.file = event.dataTransfer.files[0];
        else this.showFileErrorWindow = true
      },

      browseFileSystem() {
        this.$refs.browseInput.click()
      },

      openConfirmationWindow() {
        this.showConfirmationWindow = true
      },

      closeConfirmationWindow() {
        this.showConfirmationWindow = false
      },

      closeFileErrorWindow() {
        this.showFileErrorWindow = false
      },

      deleteLanguage() {
        this.showConfirmationWindow = false;
        this.showSpinner = true
        deleteLanguage(this.resource.id).then(res => {
          this.$emit('close', true);
        }).finally(() => { this.showSpinner = false })
      },

      clearData() {
        this.resource = {
          name: '',
          code: '',
          file: null
        }
      },

      downloadCSV() {
        let downloadFunction;
        let filename;
        if(this.isCreateForm) {
          downloadFunction = () => getCSVSampleTemplate()
          filename = "Sample language template"
        } else {
          downloadFunction = () => getCSVTemplate(this.resource.id, this.resource.code)
          filename = `${this.oldName} (${this.resource.code}).csv`
        }
        downloadFunction().then(res => {
          const blob = new Blob([res.data], {type: 'text/csv'})
          const link = document.createElement('a')

          link.href = window.URL.createObjectURL(blob)
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
      },

      createOrUpdateLanguage() {
        this.showSpinner = true
        if(!this.isCreateForm && this.oldName != this.resource.name) {
          const data = {
            locale: {
              name: this.resource.name,
              id: this.resource.id,
              code: this.resource.code
            }
          }
          updateLocaleName(this.resource.id, data).then(res => {
            if(this.resource.file != null) this.createOrUpdateFile();
            else {
              this.$emit('close', true);
              this.showSpinner = false
              this.clearData();
            }
          })
        } else if(this.resource.file != null)  this.createOrUpdateFile();
      },

      createOrUpdateFile() {
        const formData = new FormData()
        formData.append('code', this.resource.code)
        formData.append('name', this.resource.name)
        formData.append('file', this.resource.file)
        createLanguage(formData).then(res => {
          this.$emit('close', true);
          this.clearData();
        }).finally(() => { this.showSpinner = false })
      }
    },

    validations: {
      resource: {
        name: {required},
        code: {
          required,
          codeFormat(code) {
            return /^[a-z]{2}-[A-Z]{2}$/.test(code)
          }
        },
        file: {
          required: requiredIf(function() {
            return this.isCreateForm
          }) 
        }
      }
    },

    filters: {
      uppercase(text) {
        return text.toUpperCase();
      }
    }
  }
</script>

<style lang="sass" scoped>
  @import '../../../styles/main.sass'
  
  form
    display: flex
    flex-direction: column
    margin: 0 20px
    position: relative
    width: 90vw
    max-width: 550px
    transition: opacity 0.2s

    &.hide
      opacity: 0

    .exit-button
      position: absolute
      right: 5px
      top: 5px
      padding: 10px
      border: 0
      background-color: white

      .exit-icon
        width: 24px
        height: 24px
        fill: grey

    .title
      color: #0C3717
      font-size: 1.8rem
      font-weight: 400
      margin-bottom: 20px

    hr
      margin: 0 -30px
      background-color: #d4d4d4
      height: 1px;
      border: 0

    .row
      display: flex
      align-items: center

      .label
        margin-right: 20px
        font-size: 1.4rem
        min-width: 130px

      .input
        width: auto
        flex-grow: 1

    .file-container
      border: 1px dashed grey;
      display: flex
      justify-content: center
      align-items: center
      padding: 40px 30px
      width: 100%
      margin-bottom: 15px
      margin-top: 5px
      min-height: 150px;

      input
        display: none

      .file-label
        font-size: 1.3rem
        color: #0C3717
        cursor: pointer

    .file-button
      text-decoration: underline
      padding: 0
      color: #0C3717
      font-size: 1.3rem
      font-weight: 200
      cursor: pointer

    .file-uploaded-container
      display: flex
      flex-direction: column
      align-items: center

      .file-name
        font-size: 1.6rem
        text-align: center

        .done-icon
          width: 24px
          height: 24px
          fill: #8BC34A
          margin-bottom: -6px

      .file-button
        margin-top: 10px
        color: #848484

    .download-button
      margin-bottom: 30px

    .button-container
      display: flex
      flex-direction: row-reverse
      justify-content: space-between
      margin-top: 30px

      button
        padding: 12px 50px
        height: auto

        @media screen and (max-width: $screen-small)
          padding: 12px 20px

        &.create-button
          color: white

        &.delete-button
          color: $color-error
          border-color: $color-error
          border-width: 1px

</style>


// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/forms/Language.vue